<template>
  <div v-if="shouldShow" :id="`${source}_verification_section`">
    <SkeletonLoader
      v-if="gettingSubmissionStatus"
      :page="loaderPages.NUMBER_SUBMISSION_STATUS"
    />

    <div v-else class="flex">
      <CustomAlerts
        v-if="submissionStatus === registrationSubmissionStatus.FAILED"
        source="add_phone_number"
        type="warning"
      >
        <div class="flex flex-col gap-2">
          <span class="text-sm">{{
            verificationStatements[countryCode]?.failure
          }}</span>
        </div>

        <a-button
          class="secondary_medium w-fit my-2"
          @click="moveToVerifyBusiness"
        >
          Re-Submit
        </a-button>
      </CustomAlerts>

      <div v-else class="flex flex-col gap-4 p-3 rounded-lg border_8">
        <span class="text-[13px]">{{
          verificationStatements[countryCode]?.text
        }}</span>

        <div class="flex">
          <span
            v-if="submissionStatus === registrationSubmissionStatus.SUBMITTED"
            class="text-sm secondary_color"
          >
            Verification in progress....
          </span>

          <a-button
            v-else
            class="secondary_medium w-fit"
            @click="moveToVerifyBusiness"
          >
            {{ getButtonLabel }}
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  registrationSubmissionStatus,
  verificationStatements,
  countryDetails,
  twillioBundleStatus,
  countryMapping,
} from "@/src/constants/phoneNumberConstants.js";
import CallService from "@/src/api/CallService.js";
import CustomAlerts from "@/src/components/CustomAlerts.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";

const props = defineProps({
  source: { type: String, required: true },
  countryCode: { type: String, required: true },
});

const router = useRouter();
const gettingSubmissionStatus = ref(false);
const submissionStatus = ref(false);

const shouldShow = computed(() => {
  return submissionStatus.value !== registrationSubmissionStatus.COMPLETED;
});

const moveToVerifyBusiness = () => {
  try {
    router.push(`/settings/business/${props.countryCode}/manage`);
  } catch (error) {
    console.error("An error occurred while moving to verify business: ", error);
  }
};

const getButtonLabel = computed(() =>
  (props.countryCode === countryMapping.NZ &&
    submissionStatus.value === registrationSubmissionStatus.BUSINESS_ADDRESS) ||
  (props.countryCode === countryMapping.CH &&
    submissionStatus.value === registrationSubmissionStatus.BUSINESS_ADDRESS) ||
  (props.countryCode === countryMapping.UK &&
    submissionStatus.value === registrationSubmissionStatus.BUSINESS_INFORMATION)
    ? "Get Started"
    : "Continue"
);

const toGetBundleStatus = async () => {
  try {
    gettingSubmissionStatus.value = true;

    const params = {
      country_code: countryDetails[props.countryCode]?.countryCode,
    };

    const response = await CallService.getBundleStatus(params);
    const bundleStatus =
      response?.bundle_status ||
      registrationSubmissionStatus.BUSINESS_INFORMATION;
    const bundlesInfoStatus = response?.bundles_info?.[0]?.status ?? "unknown";

    submissionStatus.value = bundleStatus;
    if (bundleStatus === registrationSubmissionStatus.SUBMITTED) {
        const statusMap = {
          [twillioBundleStatus.TWILIO_REJECTED]: registrationSubmissionStatus.FAILED,
          [twillioBundleStatus.TWILIO_APPROVED]: registrationSubmissionStatus.COMPLETED,
          [twillioBundleStatus.PROVISIONALLY_APPROVED]: registrationSubmissionStatus.COMPLETED,
        };
       const mappedStatus = statusMap[bundlesInfoStatus];
       if (mappedStatus) {
         submissionStatus.value = mappedStatus;
       }
    }
  } catch (error) {
    console.error("An error occurred while getting bundle status: ", error);
  } finally {
    gettingSubmissionStatus.value = false;
  }
};

watch(
  () => props.countryCode,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      toGetBundleStatus();
    }
  }
);

onMounted(() => {
  toGetBundleStatus();
});
</script>
